import { Registry } from '@/cms/blocks/registry';
import type { BlockBase } from '@/cms/blocks/registry/types';
import { hasExactlyOneRef, MediaRef } from '@/cms/lib/mediaShared';
import { ENFORCE_EMBED_PROVIDER, REQUIRE_IMAGE_ALT } from '@/cms/lib/policy';
import '@/cms/blocks/registry/definitions';

export type ValidationIssue = { blockId?: string; field?: string; message: string };
export type ValidationResult = { ok: true } | { ok: false; issues: ValidationIssue[] };

function isImageBlock(
  b: BlockBase
): b is BlockBase<'image', MediaRef & { alt?: string; variant?: 'default' | 'hero' }> {
  return b.type === 'image';
}

function isEmbedBlock(b: BlockBase): b is BlockBase<'embed', { url: string }> {
  return b.type === 'embed';
}

export function validateBlocks(blocks: BlockBase[]): ValidationResult {
  const issues: ValidationIssue[] = [];

  for (const b of blocks) {
    const def = Registry.get(b.type);
    if (!def) {
      issues.push({ blockId: b.id, message: `Unknown block type: ${b.type}` });
      continue;
    }

    try {
      def.validate(b.data);
    } catch (err: any) {
      issues.push({ blockId: b.id, message: `Validation error: ${err?.message ?? String(err)}` });
    }

    if (isImageBlock(b)) {
      const ref = b.data as unknown as MediaRef;
      if (!hasExactlyOneRef(ref)) {
        issues.push({
          blockId: b.id,
          field: 'assetId|url',
          message: 'image must have exactly one of assetId or url'
        });
      }

      if (REQUIRE_IMAGE_ALT) {
        const alt = (b.data as any)?.alt;
        if (typeof alt !== 'string' || alt.trim().length === 0) {
          issues.push({
            blockId: b.id,
            field: 'alt',
            message: 'image alt text is required'
          });
        }
      }
    }

    if (isEmbedBlock(b) && ENFORCE_EMBED_PROVIDER) {
      const provider = b.meta?.embedProvider;
      if (!provider) {
        issues.push({
          blockId: b.id,
          field: 'meta.embedProvider',
          message: 'embedProvider must be set (e.g., "youtube")'
        });
      }
    }
  }

  return issues.length ? { ok: false, issues } : { ok: true };
}

export type PublishCheckInput = {
  locale: 'en' | 'fa';
  title?: string | null;
  slug?: string | null;
  blocks: BlockBase[];
};

export function validatePublish(input: PublishCheckInput): ValidationResult {
  const issues: ValidationIssue[] = [];

  if (!input.slug || !input.slug.trim()) {
    issues.push({ field: `slug_${input.locale}`, message: `slug_${input.locale} is required` });
  }

  if (!input.title || !input.title.trim()) {
    issues.push({ field: `title_${input.locale}`, message: `title_${input.locale} is required` });
  }

  const blocksResult = validateBlocks(input.blocks);
  if (!blocksResult.ok) {
    issues.push(...blocksResult.issues);
  }

  return issues.length ? { ok: false, issues } : { ok: true };
}
